/**
 * Real-time notification types
 */

export type NotificationType =
  // Messaging
  | 'message.new'
  | 'message.mentioned'
  | 'message.updated'
  | 'message.deleted'
  | 'conversation.created'
  | 'conversation.updated'
  | 'conversation.member_added'
  | 'conversation.member_removed'
  | 'typing.indicator'
  // Tasks
  | 'item.created'
  | 'item.updated'
  | 'item.assigned'
  | 'item.comment'
  // Pipeline — a deal's stage moved. `deal.won` / `deal.lost` REPLACE
  // `deal.stage_changed` on a terminal transition (the backend publishes one or
  // the other, never both), so anything subscribing to stage changes must handle
  // all three or it goes quiet exactly when the deal closes.
  | 'deal.stage_changed'
  | 'deal.won'
  | 'deal.lost'
  | 'outreach_task.created'
  // Inventory — AI customer-aware demand predictions (W9)
  | 'inventory.demand_predicted'
  // Recruiting
  | 'application.status_changed'
  // Support
  | 'ticket.message.new'
  // Shipments — ocean container tracking
  | 'shipment.tracking_milestone'
  // Entity following
  | 'entity.updated.followed'
  | 'entity.deleted.followed'
  // Unified entity comment system (shared.comments)
  | 'comment.added'
  | 'comment.mentioned'
  // Generic entity message/comment thread live-refresh (useThreadLiveRefresh)
  | 'entity.thread.updated'
  // A background job-queue job reached a terminal state (useJobSettledRefresh)
  | 'job.settled'
  // Onboarding Hub checklist changed — refetch progress + tasks
  | 'onboarding.updated'
  // AI Agents
  | 'agent.approval.requested'
  | 'agent.approval.resolved'
  | 'agent.approval.escalated'
  | 'agent.approval.expired'
  | 'agent.memory_updated'
  | 'scheduled_task.failed'
  | 'scheduled_task.completed'
  // OAuth / Third-party integrations
  | 'oauth.connection_expired'
  // System
  | 'system.announcement';

/**
 * The four backend delivery channels (see `shared/notifications/schemas.py`).
 * `in_app` = toast/bell, `desktop` = OS pop-up, `mobile_push` = FCM, `email`.
 */
export type NotificationChannel = 'in_app' | 'desktop' | 'mobile_push' | 'email';

export interface Notification {
  id: string;
  type: NotificationType;
  tenant_id: string;
  actor_user_id?: string;
  timestamp: string;
  entity_type?: string;
  entity_id?: string;
  title?: string;
  body?: string;
  /**
   * Delivery channels enabled for this recipient+event, surfaced by the backend
   * (`shared/notifications/schemas.py` → `Notification.channels`). The generic
   * frontend handler renders a desktop pop-up only when `'desktop'` is present
   * (focus permitting). `null`/undefined = legacy direct-`notify.*` path: WS
   * receipt implies the notification is `in_app` + `desktop`-eligible.
   */
  channels?: NotificationChannel[] | null;
  /**
   * "Alert me even though I am the actor." Both this client and the backend
   * independently skip a notification whose `actor_user_id` is the recipient, so an
   * event that legitimately needs delivering to its own actor has to say so on the
   * payload — otherwise the backend's opt-in keeps only the bell entry and the email,
   * and the toast/pop-up/sound is dropped here.
   *
   * Set for an event whose "actor" is routinely an AUTOMATION carrying the recipient's
   * own credentials (an API key authenticates as its owner, so an agent acts as the
   * human whose key it holds) — e.g. `work_item.completed_creator`. Absent/false is
   * the default and the behaviour for every other notification.
   */
  notify_actor?: boolean;
  data: Record<string, unknown>;
}

export type NotificationHandler = (notification: Notification) => void;

/**
 * A data-change cue — "something in this module changed", nothing more.
 *
 * Deliberately NOT a `Notification`: it has no id, no human copy, is never
 * persisted, and by design carries **no entity ids and no field values**
 * (`shared/data_freshness/publisher.py` — a tenant-scoped channel reaches users
 * who may not have access to the record, so an id would leak its existence).
 * The field list mirrors the frozen backend payload exactly.
 */
export interface DataChangeCue {
  /** Module id as keyed in `modules_entities.config.json`. */
  module: string;
  /** Entity config keys touched — observability only, never correctness. */
  entity_types: string[];
  /** `create` | `update` | `delete` — observability only. */
  ops: string[];
  ts: string;
  /** Reserved for the deferred echo-suppression phase; always null today. */
  origin_client_id: string | null;
}

export type DataChangeCueHandler = (cue: DataChangeCue) => void;

/**
 * True when a raw WebSocket payload is a data-change cue rather than a
 * `Notification`.
 *
 * Cues are the only server→client message with no `type` discriminator (the
 * backend payload is frozen and a contract test asserts its exact key set), so
 * the check is structural: no `type`, plus the two fields every cue carries.
 */
export function isDataChangeCue(payload: unknown): payload is DataChangeCue {
  if (typeof payload !== 'object' || payload === null) return false;
  const candidate = payload as Record<string, unknown>;
  return (
    candidate.type === undefined &&
    typeof candidate.module === 'string' &&
    Array.isArray(candidate.entity_types)
  );
}

export interface NotificationContextType {
  isConnected: boolean;
  lastNotification: Notification | null;
  subscribe: (type: NotificationType | '*', handler: NotificationHandler) => () => void;
  /**
   * Register interest in a specific entity's message/comment thread so the
   * backend delivers scoped `entity.thread.updated` cues for it to this
   * connection (see `useThreadLiveRefresh`). Refcounted — multiple viewers of
   * the same entity share one server-side subscription; re-registered
   * automatically after a WebSocket reconnect. Returns an unregister function.
   */
  subscribeThread: (entityId: string) => () => void;
  /**
   * Register interest in a module's data-change cues. Refcounted exactly like
   * `subscribeThread` — N mounted queries in one module share one server-side
   * registration — and re-registered after a WebSocket reconnect. Returns an
   * unregister function.
   *
   * Application code never calls this: `DataFreshnessProvider` drives it off
   * the React Query cache so pages need no opt-in.
   */
  subscribeChanges: (module: string) => () => void;
  /**
   * Listen for data-change cues on any subscribed module. Returns an
   * unsubscribe function.
   */
  onDataChange: (handler: DataChangeCueHandler) => () => void;
}
